import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { NgbActiveModal, NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import { DriverInformationSettings } from "../../../../../_shared/models/drivers/management/driver-information-settings.model";
import { DriverInformationService } from "../../../../../_shared/services";
import { ToastrService } from "ngx-toastr";
import { ErrorMessages } from "../../../../../_shared/constants";
import { addSeconds, differenceInSeconds, format, parse } from "date-fns";
import { DurationPickerInputComponent } from "../../../../driver-manager/components/duration-picker-input/duration-picker-input.component";
import { Subject } from "rxjs";
import { FlatpickrDatePickerComponent } from "../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component";
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-information-settings-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaterialTimepickerModule,
    NgbTooltipModule,
    DurationPickerInputComponent,
    FlatpickrDatePickerComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './driver-information-settings-modal.html',
  styleUrl: './driver-information-settings-modal.scss',
})
export class DriverInformationSettingsComponent implements OnInit {
  public isTimeModalOpen = false;
  public isLoading: boolean = false;
  public amount: string = '';
  public workingHours: string;
  public startTime: string = '';
  public endTime: string = '';
  public shiftDuration: string = '';
  public hourlyRate: string = '';
  public daysIn: string = '';
  public daysOff: string = '';
  public shiftStartDate: string = '';
  public driverSettings: DriverInformationSettings;
  public settingsForm: FormGroup;
  public globalDriverSettings: any;
  public isShiftTimeEnabled: boolean = false;
  public shiftDurationSeconds: number = 0;

  // Store previous shift time values to restore when toggling back on
  private previousWorkingHours: string = '';
  private previousStartTime: string = '';
  private previousEndTime: string = '';

  private readonly TIME_FORMAT = 'HH:mm';
  private driverId: number = 0;

  private readonly cancelSubject = new Subject<void>();
  private activeModal = inject(NgbActiveModal);
  private driverInformationService = inject(DriverInformationService);
  private toastr = inject(ToastrService);
  private fb = inject(FormBuilder);
  private transloco = inject(TranslocoService);

  async ngOnInit() {
    this.initializeForm();

    await this.getDriverSettingsAsync();
    const shiftDate = new Date(this.driverSettings?.shiftStartDate || new Date('2025-01-01T00:00:00Z'))
    if (!this.driverSettings) {
      this.driverSettings = await this.createDefaultDriverSettings();
      this.shiftDurationSeconds = this.driverSettings.shiftDuration;
    } else {
      this.amount += this.driverSettings.compensation;
      this.startTime = this.driverSettings.startTime ? this.driverSettings.startTime.split(":").slice(0, 2).join(":") : ''; // removing the appended seconds(00) that was added when saving.
      this.endTime = this.startTime ? this.getEndTime(this.startTime, this.driverSettings.shiftDuration) : '';
      this.workingHours = this.startTime && this.endTime ? `${this.startTime} - ${this.endTime}` : '';
      this.hourlyRate += this.driverSettings.holidayRate;
      this.shiftDuration = Math.round(this.driverSettings.shiftDuration / 3600).toString();
      this.shiftDurationSeconds = this.driverSettings.shiftDuration || 0;
      this.daysIn += this.driverSettings.daysIn;
      this.daysOff += this.driverSettings.daysOff,
        this.shiftStartDate += this.driverSettings.shiftStartDate
      this.isShiftTimeEnabled = this.driverSettings.isShiftTimeEnabled;
    }

    this.settingsForm.patchValue({
      workingHours: this.isShiftTimeEnabled ? (this.workingHours ?? '') : '',
      standardRate: this.driverSettings?.standardRate || '',
      saturdayRate: this.driverSettings?.saturdayRate || '',
      holidayRate: this.driverSettings?.holidayRate || '',
      daysOff: this.driverSettings?.daysOff || '',
      daysIn: this.driverSettings?.daysIn || '',
      shiftDuration: this.shiftDurationSeconds,
      hourlyRate: this.driverSettings?.hourlyRate || '',
      shiftStartDate: shiftDate.toISOString().split('T')[0],
      isShiftTimeEnabled: this.isShiftTimeEnabled
    });

    // Update workingHours validation based on final toggle state
    const workingHoursControl = this.settingsForm.get('workingHours');
    const shiftDurationControl = this.settingsForm.get('shiftDuration');

    if (this.isShiftTimeEnabled) {
      workingHoursControl?.setValidators(Validators.required);
      shiftDurationControl?.disable(); // Disable when shift time is enabled
    } else {
      workingHoursControl?.clearValidators();
      shiftDurationControl?.enable(); // Enable when shift time is disabled
    }
    workingHoursControl?.updateValueAndValidity();
  }

  initializeForm() {
    this.settingsForm = this.fb.group({
      workingHours: [this.workingHours || '', this.isShiftTimeEnabled ? Validators.required : []],
      startTime: [this.startTime || ''],
      endTime: [this.endTime || ''],
      standardRate: [this.driverSettings?.standardRate || '', Validators.required],
      saturdayRate: [this.driverSettings?.saturdayRate || '', Validators.required],
      holidayRate: [this.driverSettings?.holidayRate || '', Validators.required],
      daysIn: [this.driverSettings?.daysIn || '', Validators.required],
      daysOff: [this.driverSettings?.daysOff || '', Validators.required],
      shiftDuration: [this.shiftDurationSeconds || '', Validators.required],
      hourlyRate: [this.driverSettings?.hourlyRate || '', Validators.required],
      shiftStartDate: [this.driverSettings?.shiftStartDate || '', Validators.required],
      isShiftTimeEnabled: [this.isShiftTimeEnabled || false]
    });
  }

  async createDefaultDriverSettings(): Promise<DriverInformationSettings> {
    this.cancelSubject.next();
    const response = await this.driverInformationService.getGlobalDriverSettings(this.cancelSubject);
    this.globalDriverSettings = response?.data;
    return {
      driverId: this.driverId,
      compensation: 1,
      startTime: "00:00:00",
      shiftDuration: 32400,
      standardRate: this.globalDriverSettings.standardRate,
      saturdayRate: this.globalDriverSettings.saturdayRate,
      holidayRate: this.globalDriverSettings.holidayRate,
      hourlyRate: 79.99,
      daysIn: 4,
      daysOff: 4,
      shiftStartDate: new Date('2025-01-01T00:00:00Z'),
      isShiftTimeEnabled: false
    };
  }

  async getDriverSettingsAsync(): Promise<void> {
    try {
      this.isLoading = true;
      this.cancelSubject.next();
      const response = await this.driverInformationService.getDriverSettings(this.driverId, this.cancelSubject);
      if (response?.data) {
        this.driverSettings = response?.data;
      }
    } catch (error: any) {
      this.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async saveChangesAsync() {
    if (this.settingsForm.valid) {
      try {
        this.getFormValues();
        this.driverSettings.compensation = 1;
        this.isLoading = true;
        await this.driverInformationService.upsertDriverSettings(this.driverSettings);
          
        this.toastr.success(this.transloco.translate('driverInformation.toasts.settingsSaved'));
      } catch (error: any) {
        const message = ErrorMessages[error?.error?.errorCode] || ErrorMessages['errors/default'];
        this.toastr.error(message);
      } finally {
        this.isLoading = false;
        this.close();
      }
    }
  }

  getFormValues() {
    const formValues = this.settingsForm.value;
    this.driverSettings.standardRate = formValues.standardRate;
    this.driverSettings.holidayRate = formValues.holidayRate;
    this.driverSettings.saturdayRate = formValues.saturdayRate;
    this.driverSettings.hourlyRate = Number(formValues.hourlyRate);
    this.driverSettings.shiftDuration = this.settingsForm.get('shiftDuration')?.value ?? 0;
    this.driverSettings.daysIn = Number(formValues.daysIn);
    this.driverSettings.daysOff = Number(formValues.daysOff);
    this.driverSettings.shiftStartDate = new Date(formValues.shiftStartDate + 'T00:00:00Z');
    this.driverSettings.isShiftTimeEnabled = formValues.isShiftTimeEnabled;

    // Only set time-related values if shift time is enabled
    if (formValues.isShiftTimeEnabled && formValues.startTime && formValues.endTime) {
      this.driverSettings.startTime = formValues.startTime + ':00';
    }
  }

  close() {
    this.activeModal.close();
  }

  getTotalSeconds(start: string, end: string): number {
    const startTime = parse(start, this.TIME_FORMAT, new Date());
    const endTime = parse(end, this.TIME_FORMAT, new Date());
    return differenceInSeconds(endTime, startTime);
  }

  getEndTime(start: string, totalSeconds: number): string {
    const startTime = parse(start, this.TIME_FORMAT, new Date());
    const endTime = addSeconds(startTime, totalSeconds);
    return format(endTime, this.TIME_FORMAT)
  }

  handleError(error: any) {
    const errorCode = error?.error?.errorCode;
    if (errorCode != "driver-information/no-driver-settings-found") { // No need to show this error because we no there were no settings created yet
      const message = ErrorMessages[errorCode] || ErrorMessages['errors/default'];
      this.toastr.error(message);
    }
  }

  isValidTimeRange(): boolean {
    const startTime = this.settingsForm.get('startTime')?.value;
    const endTime = this.settingsForm.get('endTime')?.value;
    if (!startTime || !endTime) {
      return false;
    }
    return this.getTotalSeconds(startTime, endTime) > 0;
  }

  saveTimeChanges() {
    const startTime = this.settingsForm.get('startTime')?.value;
    const endTime = this.settingsForm.get('endTime')?.value;
    const start = parse(startTime, this.TIME_FORMAT, new Date());
    const end = parse(endTime, this.TIME_FORMAT, new Date());

    // Calculate the difference in seconds
    const secondsDifference = differenceInSeconds(end, start);

    this.settingsForm.patchValue({
      workingHours: `${startTime} - ${endTime}`,
      shiftDuration: Math.abs(secondsDifference)
    });

    this.isTimeModalOpen = false;
  }

  toggleShiftTimeEnabled() {
    const currentValue = this.settingsForm.get('isShiftTimeEnabled')?.value;
    const newValue = !currentValue;

    this.settingsForm.get('isShiftTimeEnabled')?.setValue(newValue);

    const workingHoursControl = this.settingsForm.get('workingHours');
    const startTimeControl = this.settingsForm.get('startTime');
    const endTimeControl = this.settingsForm.get('endTime');
    const shiftDurationControl = this.settingsForm.get('shiftDuration');

    if (!newValue) {
      // Store current values before clearing them
      this.previousWorkingHours = workingHoursControl?.value || '';
      this.previousStartTime = startTimeControl?.value || '';
      this.previousEndTime = endTimeControl?.value || '';

      // Clear the working hours value to show placeholder
      workingHoursControl?.setValue('');
      startTimeControl?.setValue('');
      endTimeControl?.setValue('');

      // Remove required validation when toggle is off
      workingHoursControl?.clearValidators();

      // Enable shift duration control when shift time is disabled
      shiftDurationControl?.enable();
    } else {
      // Restore previous values when toggling back on
      if (this.previousWorkingHours) {
        workingHoursControl?.setValue(this.previousWorkingHours);
        startTimeControl?.setValue(this.previousStartTime);
        endTimeControl?.setValue(this.previousEndTime);

        // Recalculate shift duration if we have start and end times
        if (startTimeControl?.value && endTimeControl?.value) {
          const start = parse(startTimeControl.value, this.TIME_FORMAT, new Date());
          const end = parse(endTimeControl.value, this.TIME_FORMAT, new Date());
          const secondsDifference = differenceInSeconds(end, start);
          shiftDurationControl?.setValue(Math.abs(secondsDifference));
        }
      }

      // Add required validation when toggle is on
      workingHoursControl?.setValidators(Validators.required);

      // Disable shift duration control when shift time is enabled
      shiftDurationControl?.disable();
    }

    // Update validation
    workingHoursControl?.updateValueAndValidity();
  }

  toggleTimeModal(): void {
    // Only open modal if shift time is enabled
    if (!this.settingsForm.get('isShiftTimeEnabled')?.value) {
      return;
    }

    this.isTimeModalOpen = !this.isTimeModalOpen;

    if (this.isTimeModalOpen) {
      const workingHoursValue = this.settingsForm.get('workingHours')?.value;

      if (workingHoursValue && workingHoursValue.includes(' - ')) {
        const [startTime, endTime] = workingHoursValue.split(' - ');

        this.settingsForm.patchValue({
          startTime: startTime,
          endTime: endTime
        });
      }
    }
  }
}